import { useMemo, useCallback, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDsTranslate, useDsServices, useDsNotify } from '../context/DsProvider';
import { Button } from './ui/Button';
import { AddReviewersModal } from './AddReviewersModal';
import type { ReviewTargetType, ReviewStatus, ReviewerStatus } from '../types/review';
import styles from './ReviewStatusPanel.module.scss';

interface ReviewStatusPanelProps {
  targetType: ReviewTargetType;
  targetId: string;
  compact?: boolean;
  /** Progress bar only — hide Mark-Complete / Force-Complete / Cancel buttons. Compact mode only. */
  hideActions?: boolean;
  /** Called after any status-changing action (cancel, complete, mark-complete) succeeds.
   *  Use this to invalidate the parent entity's query so its status badge updates. */
  onStatusChange?: () => void;
}

function getStatusClass(status: ReviewStatus): string {
  switch (status) {
    case 'PENDING': return styles.statusPending ?? '';
    case 'IN_PROGRESS': return styles.statusInProgress ?? '';
    case 'COMPLETED': return styles.statusCompleted ?? '';
    case 'CANCELLED': return styles.statusCancelled ?? '';
    default: return '';
  }
}

function getReviewerStatusClass(status: ReviewerStatus): string {
  switch (status) {
    case 'REVIEWED': return styles.reviewerStatusReviewed ?? '';
    case 'VIEWED': return styles.reviewerStatusViewed ?? '';
    case 'SENT': return styles.reviewerStatusSent ?? '';
    case 'PENDING': default: return styles.reviewerStatusPending ?? '';
  }
}

function formatReviewerStatus(status: ReviewerStatus, t: (key: string, fallback: string) => string): string {
  switch (status) {
    case 'REVIEWED': return t('K_DS_REVIEWER_STATUS_REVIEWED', 'Reviewed');
    case 'VIEWED': return t('K_DS_REVIEWER_STATUS_VIEWED', 'Viewed');
    case 'SENT': return t('K_DS_REVIEWER_STATUS_SENT', 'Sent');
    case 'PENDING': return t('K_DS_REVIEWER_STATUS_PENDING', 'Pending');
    default: return status;
  }
}

function formatReviewStatus(status: ReviewStatus, t: (key: string, fallback: string) => string): string {
  switch (status) {
    case 'PENDING': return t('K_DS_REVIEW_STATUS_PENDING', 'Pending');
    case 'IN_PROGRESS': return t('K_DS_REVIEW_STATUS_IN_PROGRESS', 'In Progress');
    case 'COMPLETED': return t('K_DS_REVIEW_STATUS_COMPLETED', 'Completed');
    case 'CANCELLED': return t('K_DS_REVIEW_STATUS_CANCELLED', 'Cancelled');
    default: return status;
  }
}

export function ReviewStatusPanel({ targetType, targetId, compact = false, hideActions = false, onStatusChange }: ReviewStatusPanelProps) {
  const [showAddReviewers, setShowAddReviewers] = useState(false);
  const t = useDsTranslate();
  const queryClient = useQueryClient();
  const notify = useDsNotify();
  const { review: reviewService } = useDsServices();

  const { data: reviews, isLoading } = useQuery({
    queryKey: ['ds-reviews', targetType, targetId],
    queryFn: () => reviewService!.getForTarget(targetType, targetId),
    enabled: !!targetType && !!targetId && !!reviewService,
  });

  const activeReview = useMemo(() => {
    if (!reviews || reviews.length === 0) return null;
    return reviews.find((r) => r.status === 'IN_PROGRESS' || r.status === 'PENDING') ?? reviews[0] ?? null;
  }, [reviews]);

  const reviewedCount = useMemo(() => {
    if (!activeReview) return 0;
    return activeReview.reviewers.filter((r) => r.status === 'REVIEWED').length;
  }, [activeReview]);

  const totalReviewers = activeReview?.reviewers.length ?? 0;
  const progressPercent = totalReviewers > 0 ? (reviewedCount / totalReviewers) * 100 : 0;

  const cancelMutation = useMutation({
    mutationFn: (reviewId: string) => reviewService!.cancel(reviewId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ds-reviews', targetType, targetId] });
      onStatusChange?.();
      notify({ variant: 'success', title: t('K_REVIEW_CANCELLED_SUCCESS', 'Review cancelled successfully.') });
    },
    onError: () => {
      notify({ variant: 'critical', title: t('K_REVIEW_CANCELLED_ERROR', 'Failed to cancel review.') });
    },
  });

  const completeMutation = useMutation({
    mutationFn: (reviewId: string) => reviewService!.complete(reviewId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ds-reviews', targetType, targetId] });
      onStatusChange?.();
      notify({ variant: 'success', title: t('K_REVIEW_COMPLETED_SUCCESS', 'Review completed successfully.') });
    },
    onError: () => {
      notify({ variant: 'critical', title: t('K_REVIEW_COMPLETED_ERROR', 'Failed to complete review.') });
    },
  });

  const authorCompleteMutation = useMutation({
    mutationFn: (reviewId: string) => reviewService!.markAuthorComplete(reviewId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ds-reviews', targetType, targetId] });
      onStatusChange?.();
      notify({ variant: 'success', title: t('K_REVIEW_AUTHOR_COMPLETE_SUCCESS', 'Marked as complete.') });
    },
    onError: () => {
      notify({ variant: 'critical', title: t('K_REVIEW_AUTHOR_COMPLETE_ERROR', 'Failed to mark as complete.') });
    },
  });

  const anyPending = cancelMutation.isPending || completeMutation.isPending || authorCompleteMutation.isPending;
  const handleCancel = useCallback(() => { if (activeReview) cancelMutation.mutate(activeReview.id); }, [activeReview, cancelMutation]);
  const handleComplete = useCallback(() => { if (activeReview) completeMutation.mutate(activeReview.id); }, [activeReview, completeMutation]);
  const handleAuthorComplete = useCallback(() => { if (activeReview) authorCompleteMutation.mutate(activeReview.id); }, [activeReview, authorCompleteMutation]);

  if (isLoading) {
    if (compact) return null;
    return (
      <div className={styles.panel}>
        <div className={styles.loading}>{t('K_LOADING', 'Loading...')}</div>
      </div>
    );
  }

  if (!activeReview) {
    if (compact) return null;
    return (
      <div className={styles.panel}>
        <div className={styles.empty}>{t('K_REVIEW_NO_ACTIVE', 'No active reviews.')}</div>
      </div>
    );
  }

  const isActive = activeReview.status === 'PENDING' || activeReview.status === 'IN_PROGRESS';

  if (compact) {
    return (
      <div className={styles.panelInline}>
        <div className={styles.inlineProgress}>
          <div className={styles.progressBar}>
            <div className={styles.progressFill} style={{ width: `${progressPercent}%` }} />
          </div>
          <span className={styles.progressText}>
            {`${reviewedCount}/${totalReviewers} ${t('K_DS_REVIEWED', 'reviewed')}`}
          </span>
        </div>
        {isActive && !hideActions && (
          <>
            {!activeReview.authorCompleted && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleAuthorComplete}
                loading={authorCompleteMutation.isPending}
                disabled={anyPending && !authorCompleteMutation.isPending}
              >
                {t('K_BUTTON_MARK_COMPLETE', 'Mark as Complete')}
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleComplete}
              loading={completeMutation.isPending}
              disabled={anyPending && !completeMutation.isPending}
            >
              {t('K_BUTTON_FORCE_COMPLETE', 'Force Complete')}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleCancel}
              loading={cancelMutation.isPending}
              disabled={anyPending && !cancelMutation.isPending}
            >
              {t('K_BUTTON_CANCEL', 'Cancel')}
            </Button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <h3 className={styles.title}>{t('K_REVIEW_STATUS', 'Review Status')}</h3>
        <span className={`${styles.statusBadge} ${getStatusClass(activeReview.status)}`}>
          {formatReviewStatus(activeReview.status, t)}
        </span>
      </div>

      <div className={styles.progress}>
        <span className={styles.progressText}>
          {t('K_REVIEW_PROGRESS', '{reviewed} of {total} reviewed')
            .replace('{reviewed}', String(reviewedCount))
            .replace('{total}', String(totalReviewers))}
        </span>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${progressPercent}%` }} />
        </div>
      </div>

      <div className={styles.reviewerList}>
        {activeReview.reviewers.map((reviewer) => (
          <div key={reviewer.id} className={styles.reviewer}>
            <div className={styles.reviewerInfo}>
              <span className={styles.reviewerName}>{reviewer.name}</span>
              <span className={styles.reviewerEmail}>{reviewer.email}</span>
            </div>
            <span className={`${styles.reviewerStatus} ${getReviewerStatusClass(reviewer.status)}`}>
              {formatReviewerStatus(reviewer.status, t)}
            </span>
          </div>
        ))}
      </div>

      {isActive && (
        <div className={styles.actions}>
          {!activeReview.authorCompleted && (
            <Button
              variant="primary"
              size="sm"
              onClick={handleAuthorComplete}
              loading={authorCompleteMutation.isPending}
              disabled={anyPending && !authorCompleteMutation.isPending}
            >
              {t('K_BUTTON_MARK_COMPLETE', 'Mark as Complete')}
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowAddReviewers(true)}
            disabled={anyPending}
          >
            {t('K_DS_BUTTON_MANAGE_REVIEWERS', 'Manage Reviewers')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleComplete}
            loading={completeMutation.isPending}
            disabled={anyPending && !completeMutation.isPending}
          >
            {t('K_BUTTON_FORCE_COMPLETE', 'Force Complete')}
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={handleCancel}
            loading={cancelMutation.isPending}
            disabled={anyPending && !cancelMutation.isPending}
          >
            {t('K_BUTTON_CANCEL_REVIEW', 'Cancel Review')}
          </Button>
        </div>
      )}

      {showAddReviewers && activeReview && (
        <AddReviewersModal
          review={activeReview}
          targetType={targetType}
          targetId={targetId}
          onClose={() => setShowAddReviewers(false)}
        />
      )}
    </div>
  );
}
